<!-- src/app/jobs/jobs-search/jobs-search.component.html -->
<div class="px-5 pt-10 pb-15 w-full md:w-[768px] xl:w-[1280px] mx-auto">

  <!-- Breadcrumbs -->
  <nav class="flex items-center gap-2 text-sm text-gray-500 mb-6">
    <a routerLink="/" class="hover:text-gray-800 transition-colors">Home</a>
    <span>/</span>
    <span class="text-gray-800 font-medium">Jobs</span>
  </nav>

  <!-- Page Header -->
  <div class="mb-7">
    <h1 class="text-4xl font-bold leading-tight text-gray-900 m-0 mb-2">Search Jobs</h1>
    <p class="text-gray-500 text-base m-0">
      Filter open positions by experience, location, and job type.
    </p>
  </div>

  <!-- Ad Slot — Above Filter Form -->
  <div class="mb-4">
    <app-ad-slot [slotUrl]="adSlotUrl" />
  </div>

  <!-- Filter Form -->
  <form
    [formGroup]="filterForm"
    (ngSubmit)="onSearch()"
    class="bg-white md:border md:border-gray-200 md:rounded-2xl p-0 md:p-6 mb-9 shadow-xs"
  >

    <!-- Mobile toggle -->
    <button
      type="button"
      class="w-full flex items-center justify-between px-4 py-3.5 mb:0 md:mb-3 border border-gray-200 rounded-xl bg-white text-base font-bold text-gray-800 md:hidden"
      (click)="toggleFilters()"
      [attr.aria-expanded]="filtersOpen()"
    >
      <span>{{ filtersOpen() ? 'Hide Filters' : 'Show Filters' }}</span>
      <mat-icon class="transition-transform duration-200" [class.rotate-180]="filtersOpen()" svgIcon="chevron-down">
        
      </mat-icon>
    </button>

    <!-- Filter content -->
    @if (filtersOpen()) {
      <div>

        <!-- Row 1: Keyword, City/State, State, Company -->
        <div class="flex gap-4 flex-wrap mb-4">

          <!-- Keyword -->
          <mat-form-field appearance="outline" class="flex-[2] min-w-60">
            <mat-label>Keyword</mat-label>
            <input matInput formControlName="q" placeholder="Job title, keyword..." />
          </mat-form-field>

          <!-- City / State geocode -->
          <div class="flex-1 min-w-52 relative">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>City / State</mat-label>
              <input
                matInput
                [value]="locationLabel()"
                (input)="onLocationInput($any($event.target).value)"
                (blur)="closeSuggestions()"
                placeholder="e.g. Nashville, TN"
                autocomplete="off"
              />
              @if (locationStatus() === 'loading') {
                <mat-icon matSuffix class="text-gray-400 animate-spin" svgIcon="loader-circle"></mat-icon>
              } @else if (locationStatus() === 'found') {
                <mat-icon matSuffix class="text-green-500" svgIcon="circle-check"></mat-icon>
              } @else if (locationStatus() === 'error') {
                <mat-icon matSuffix class="text-red-400" svgIcon="circle-x"></mat-icon>
              }
            </mat-form-field>

            <!-- Suggestions dropdown -->
            @if (!hideSuggestions() && locationSuggestions().length > 0) {
              <div class="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden mt-1">
                @for (suggestion of locationSuggestions(); track suggestion.id) {
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                    (mousedown)="selectSuggestion(suggestion)"
                  >
                    <span class="font-semibold text-gray-900">{{ suggestion.text }}</span>
                    <span class="text-gray-400 ml-1">{{ suggestion.context }}</span>
                  </button>
                }
              </div>
            }
          </div>

          <!-- State -->
          <mat-form-field appearance="outline" class="flex-1 min-w-44">
            <mat-label>State</mat-label>
            <mat-select formControlName="hiringState">
              <mat-option value="">All States</mat-option>
              @for (state of stateOptions; track state.key) {
                <mat-option [value]="state.key">{{ state.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <!-- Company -->
          <mat-form-field appearance="outline" class="flex-1 min-w-52">
            <mat-label>Company</mat-label>
            <mat-select formControlName="company">
              <mat-option value="">All Companies</mat-option>
              @for (company of companies(); track company.id) {
                <mat-option [value]="company.slug">{{ company.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

        </div>

        <!-- Row 2: CDL, Driver Type, Freight, Endorsement, Experience -->
        <div class="flex gap-4 flex-wrap mb-4">

          <mat-form-field appearance="outline" class="flex-1 min-w-36">
            <mat-label>CDL Class</mat-label>
            <mat-select formControlName="cdlClass">
              <mat-option value="">Any</mat-option>
              @for (opt of cdlOptions; track opt.key) {
                <mat-option [value]="opt.key">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1 min-w-44">
            <mat-label>Driver Type</mat-label>
            <mat-select formControlName="driverType">
              <mat-option value="">Any</mat-option>
              @for (opt of driverTypeOptions; track opt.key) {
                <mat-option [value]="opt.key">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1 min-w-44">
            <mat-label>Freight Type</mat-label>
            <mat-select formControlName="freightType">
              <mat-option value="">Any</mat-option>
              @for (opt of freightTypeOptions; track opt.key) {
                <mat-option [value]="opt.key">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1 min-w-44">
            <mat-label>Endorsement</mat-label>
            <mat-select formControlName="endorsement">
              <mat-option value="">Any</mat-option>
              @for (opt of endorsementOptions; track opt.key) {
                <mat-option [value]="opt.key">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1 min-w-44">
            <mat-label>Experience</mat-label>
            <mat-select formControlName="experienceType">
              <mat-option value="">Any</mat-option>
              @for (opt of experienceOptions; track opt.key) {
                <mat-option [value]="opt.key">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

        </div>

        <!-- Row 3: Zip, Radius, Use My Location -->
        <div class="flex gap-4 flex-wrap items-end mb-6">

          <!-- Zip -->
          <mat-form-field appearance="outline" class="w-40">
            <mat-label>Zip Code</mat-label>
            <input
              matInput
              formControlName="zip"
              placeholder="e.g. 90210"
              maxlength="5"
            />
          </mat-form-field>

          <!-- Radius Slider -->
          <div class="flex-1 min-w-56 pb-1">
            <div class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              Radius: {{ filterForm.get('radius')?.value }} miles
            </div>
            <mat-slider
              min="10"
              max="500"
              step="10"
              [disabled]="!radiusEnabled()"
              class="w-full"
            >
              <input matSliderThumb formControlName="radius" />
            </mat-slider>
            <div class="flex justify-between text-xs text-gray-400 -mt-1">
              <span>10 mi</span>
              <span>500 mi</span>
            </div>
          </div>

          <!-- Use My Location -->
          <div class="pb-1">
            <button
              type="button"
              mat-stroked-button
              [disabled]="geoLocating()"
              (click)="useMyLocation()"
              class="whitespace-nowrap"
              [class.!border-blue-500]="geoSet()"
              [class.!text-blue-600]="geoSet()"
            >
              @if (geoLocating()) {
                <div class="flex flex-row gap-1 items-center">
                  <mat-icon class="animate-spin" svgIcon="refresh"></mat-icon>
                  <div>Locating...</div>
                </div>
                
              } @else if (geoSet()) {
                <div class="flex flex-row gap-1 items-center">
                  <mat-icon svgIcon="circle-check"></mat-icon>
                  <div>Location Set</div>
                </div>
                
              } @else {
                <div class="flex flex-row gap-1 items-center">
                  <mat-icon svgIcon="locate-fixed"></mat-icon>
                <div>Use My Location</div>
                </div>
              }
            </button>
          </div>

        </div>

        <!-- Actions -->
        <div class="flex justify-between items-center gap-4 flex-wrap">
          <div class="flex gap-3 items-center">
            <button mat-flat-button color="primary" type="submit">
              Search Jobs
            </button>
            <button
              type="button"
              mat-button
              class="!text-gray-500"
              (click)="clearFilters()"
            >
              Clear filters
            </button>
          </div>

          @if (totalJobs() > 0) {
            <div class="text-sm text-gray-500">
              {{ totalJobs() | number }} job{{ totalJobs() !== 1 ? 's' : '' }} found
            </div>
          }
        </div>

      </div>
    }

  </form>

  <!-- Loading -->
  @if (loading()) {
    <div class="flex justify-center items-center py-20">
      <mat-spinner diameter="48" />
    </div>
  }

  <!-- No Results -->
  @else if (!loading() && jobs().length === 0) {
    <p class="text-gray-400 text-center py-10">
      No jobs found matching your criteria. Try adjusting your filters.
    </p>
  }

  <!-- Results -->
  @else {
    <div class="text-sm text-gray-500 mb-5">
      Showing {{ showingCount() }} result{{ showingCount() !== 1 ? 's' : '' }}
      @if (currentPage() > 1) {
        — Page {{ currentPage() }}
      }
    </div>

    <div class="grid gap-5 items-stretch mb-10"
         style="grid-template-columns: repeat(auto-fill, minmax(260px, 1fr))">
      @for (job of jobs(); track job.id) {
        <app-job-search-card [job]="job" />
      }
    </div>

    <!-- Pagination -->
    <div class="flex justify-center items-center gap-3 flex-wrap">

      @if (currentPage() > 1) {
        <button
          mat-stroked-button
          type="button"
          (click)="goToPage(currentPage() - 1)"
        >
          ← Previous
        </button>
      }

      <span class="text-sm text-gray-500">
        Page {{ currentPage() }} of {{ totalPages() }}
      </span>

      @if (currentPage() < totalPages()) {
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="goToPage(currentPage() + 1)"
        >
          Next →
        </button>
      }

    </div>
  }

  <!-- Ad Slot — Footer -->
  <div class="mt-10">
    <app-ad-slot [slotUrl]="adSlotUrl" />
  </div>

</div>

